import React from 'react';
import { Button, Input, InputNumber, Tooltip, Typography } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import VariantPicker from '../components/inventory/VariantPicker';
import { formatCurrency, formatCurrencyUnitPrice } from '../utils/currency';
import { formatUnit } from '../utils/units';
import { formatConversionValue } from '../utils/dimensionConversion';
import { formatProductDimensions } from '../utils/productDimensions';
import { POLISH_DECIMAL_INPUT_NUMBER_PROPS } from '../utils/polishNumberInput';
import { UNIT_PRICE_INPUT_PROPS } from '../utils/money';
import { PZ_QUANTITY_MAX, WAREHOUSE_QUANTITY_PRECISION } from '../utils/warehouseQuantity';
import { sanitizeProductDisplayName } from '../utils/productSelectorLabel';
import type { PZItem, ProductVariant } from '../types/pz';
import WarehouseQuantityField from '../modules/warehouse/WarehouseQuantityField';
import { isPZItemProductTraceLocked } from '../modules/warehouse/pzTraceLock';
import {
  WAREHOUSE_CONVERTED_QUANTITY_MAX,
  WarehouseQuantityPatch,
  formatWarehouseQuantity,
  getWarehouseLineUnit,
} from '../modules/warehouse/warehouseQuantityConversion';
import './PZItemsSection.css';

export interface PZItemRow extends Partial<PZItem> {
  key?: string;
}

interface PZItemsSectionProps {
  items: PZItemRow[];
  isEditMode: boolean;
  canViewPurchasePrices: boolean;
  canEditPurchasePrices: boolean;
  /** The page enables this for drafts and explicit received-document corrections. */
  canEditQuantityReceived: boolean;
  /** Received document (correction): a ZD-traced line keeps its product. */
  documentReceived?: boolean;
  /** Manual receipt: the line quantity is the received quantity, so the
   *  separate "Przyjęto" field is not shown (see isPZReceiptQuantityOnly). */
  receiptQuantityOnly?: boolean;
  /** Rendered as-is; PZDetail owns the derivation (formatWarehouseQuantityTotals). */
  quantityTotalsLabel: string;
  totalValue: { amount: number; currency: string };
  isInvoiceReceipt?: boolean;
  acceptedReceiptCostPLN?: number;
  onItemChange: (key: string, field: keyof PZItemRow, value: unknown) => void;
  /** Quantity and conversionUnit are chosen together in the conversion modal. */
  onQuantityChange: (key: string, patch: WarehouseQuantityPatch) => void;
  onVariantSelect: (key: string, variantId: string | undefined, variant?: ProductVariant) => void;
  onRemoveItem: (key: string) => void;
}

const formatLineDimensions = (item: PZItemRow) => formatProductDimensions({
  width: item.width,
  height: item.height,
  length: item.length,
  thickness: item.thickness,
  productCategoryDimensionDisplayProfile:
    item.productCategoryDimensionDisplayProfile ?? 'GENERIC',
}) || '-';

const PZItemsSection: React.FC<PZItemsSectionProps> = ({
  items,
  isEditMode,
  canViewPurchasePrices,
  canEditPurchasePrices,
  canEditQuantityReceived,
  documentReceived = false,
  receiptQuantityOnly = false,
  quantityTotalsLabel,
  totalValue,
  isInvoiceReceipt = false,
  acceptedReceiptCostPLN,
  onItemChange,
  onQuantityChange,
  onVariantSelect,
  onRemoveItem,
}) => {
  const { t, i18n } = useTranslation(['pz', 'common']);

  if (items.length === 0) {
    return <div className="pz-items-empty">{t('pz:noItems')}</div>;
  }

  return (
    <div className="pz-items-section" data-testid="pz-items-card-layout">
      <div className="pz-items-list">
        {items.map((item, index) => {
          const itemKey = item.key || String(index);
          // Supplier-order trace (WP12 1.18): only the product of a traced
          // line on a received PZ is locked. Quantity is always editable, and
          // on a draft the product may change after a confirmed warning. The
          // remove button is NOT governed by this lock.
          const productTraceLocked = isPZItemProductTraceLocked(item, { documentReceived });
          // The receipt quantity is denominated in this line's selected unit, so
          // conversion must start there; otherwise an m2 input would be multiplied
          // by the board area a second time.
          const conversionLabel = formatConversionValue(
            item.conversionUnit,
            { ...item, unit: getWarehouseLineUnit({ unit: item.unit }) },
            item.quantityOrdered || 0,
          );
          const pricingQuantity = item.purchaseQuantity ?? item.quantityOrdered ?? 0;
          const pricingUnit = item.purchaseUnit || item.unit;
          const lineTotal = !isEditMode && item.purchaseInvoiceItemId && item.totalPrice != null
            ? item.totalPrice : pricingQuantity * (item.unitPrice || 0);
          const productLabel = sanitizeProductDisplayName(
            item.productName || item.productVariant?.product?.name,
            [
              item.productCode,
              item.sku,
              item.ean,
              item.productVariantId,
              item.productVariant?.id,
              item.productVariant?.sku,
              item.productVariant?.product?.id,
              item.productVariant?.product?.code,
              item.productVariant?.product?.ean,
            ],
          );

          return (
            <article className="pz-item-card" key={itemKey} data-testid="pz-item-card">
              <div className="pz-item-card__header">
                <div className="pz-item-card__number">
                  {t('pz:itemFields.position', { number: index + 1 })}
                </div>
                {isEditMode && (
                  <Button
                    className="pz-item-card__remove"
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => onRemoveItem(itemKey)}
                    disabled={items.length === 1}
                    aria-label={t('common:delete')}
                  >
                    {t('common:delete')}
                  </Button>
                )}
              </div>

              <div className="pz-item-card__product">
                <Typography.Text className="pz-item-field__label">
                  {t('pz:itemFields.productVariant')}
                </Typography.Text>
                {isEditMode ? (
                  <VariantPicker
                    purchasePriceScope="warehouse-document"
                    value={item.productVariantId || undefined}
                    disabled={productTraceLocked}
                    onChange={(variantId, variant) => onVariantSelect(itemKey, variantId, variant)}
                    placeholder={t('pz:actions.selectVariant')}
                    style={{ width: '100%' }}
                  />
                ) : (
                  <Typography.Text className="pz-item-card__product-name">
                    {productLabel || '-'}
                  </Typography.Text>
                )}
              </div>

              <div className="pz-item-card__details">
                <div className="pz-item-field pz-item-field--ean">
                  <Typography.Text className="pz-item-field__label">
                    {t('pz:itemFields.ean')}
                  </Typography.Text>
                  {isEditMode ? (
                    <Input
                      aria-label={t('pz:itemFields.ean')}
                      value={item.ean}
                      maxLength={64}
                      placeholder={t('pz:itemFields.ean')}
                      onChange={(event) => onItemChange(itemKey, 'ean', event.target.value)}
                    />
                  ) : (
                    <Typography.Text>{item.ean || '-'}</Typography.Text>
                  )}
                </div>

                <div className="pz-item-field pz-item-field--dimensions">
                  <Typography.Text className="pz-item-field__label">
                    {t('pz:itemFields.dimensionsMm')}
                  </Typography.Text>
                  <Typography.Text>{formatLineDimensions(item)}</Typography.Text>
                </div>

                <div className="pz-item-field pz-item-field--quantity">
                  <Typography.Text className="pz-item-field__label">
                    {t(receiptQuantityOnly ? 'pz:itemFields.quantityReceived' : 'pz:itemFields.quantityOrdered')}
                  </Typography.Text>
                  {isEditMode ? (
                    <WarehouseQuantityField
                      line={{
                        ...item,
                        quantity: item.quantityOrdered,
                      }}
                      onChange={(patch) => onQuantityChange(itemKey, patch)}
                      pricingQuantity={item.purchaseQuantity}
                      pricingUnit={item.purchaseUnit}
                      maxQuantity={PZ_QUANTITY_MAX}
                      maxConvertedQuantity={WAREHOUSE_CONVERTED_QUANTITY_MAX}
                      noDimensionsLabel={t('pz:conversion.noDimensions')}
                      unitLabel={t('pz:itemFields.unit')}
                    />
                  ) : (
                    <Typography.Text strong>
                      {formatWarehouseQuantity(item.quantityOrdered, item.unit, i18n.language)}
                    </Typography.Text>
                  )}
                </div>

                {!receiptQuantityOnly && (
                  <div className="pz-item-field pz-item-field--quantity-received">
                    <Typography.Text className="pz-item-field__label">
                      {t('pz:itemFields.quantityReceivedLabel')}
                    </Typography.Text>
                    {isEditMode && canEditQuantityReceived ? (
                      <InputNumber
                        aria-label={t('pz:itemFields.quantityReceivedLabel')}
                        // Deliberately no min/max clamp: antd silently coerces
                        // an out-of-range entry on blur (e.g. -50 -> 0), and 0
                        // is itself a legitimate, distinct value here ("stock
                        // zero units") — the exact silent-coercion bug this
                        // repo's numeric-input rule warns about. Negative or
                        // imprecise values remain hard validation errors;
                        // over-receipt is confirmed during save.
                        step={0.001}
                        precision={WAREHOUSE_QUANTITY_PRECISION}
                        value={item.quantityReceived ?? undefined}
                        placeholder={t('pz:itemFields.quantityReceivedPlaceholder')}
                        // Empty (cleared) field -> antd emits null -> "not
                        // entered", independent of quantityOrdered; the backend
                        // falls back to the ordered quantity at receive time.
                        onChange={(value) => onItemChange(itemKey, 'quantityReceived', value)}
                        addonAfter={formatUnit(item.unit, i18n.language)}
                        style={{ width: '100%' }}
                        {...POLISH_DECIMAL_INPUT_NUMBER_PROPS}
                      />
                    ) : (
                      <Typography.Text>
                        {item.quantityReceived == null
                          ? t('pz:itemFields.quantityReceivedPlaceholder')
                          : formatWarehouseQuantity(item.quantityReceived, item.unit, i18n.language)}
                      </Typography.Text>
                    )}
                  </div>
                )}

                {!isEditMode && (
                  <div className="pz-item-field pz-item-field--conversion">
                    <Typography.Text className="pz-item-field__label">
                      {t('pz:itemFields.conversion')}
                    </Typography.Text>
                    <Typography.Text>
                      {item.conversionUnit
                        ? (conversionLabel || t('pz:conversion.noDimensions'))
                        : '-'}
                    </Typography.Text>
                  </div>
                )}

                {canViewPurchasePrices && (
                  <>
                    <div className="pz-item-field pz-item-field--unit-price">
                      <Typography.Text className="pz-item-field__label">
                        {t('pz:itemFields.unitPrice')}
                      </Typography.Text>
                      {isEditMode && canEditPurchasePrices ? (
                        <Tooltip title={t('common:unitPricePrecisionHint')}>
                          <InputNumber
                            aria-label={t('pz:itemFields.unitPrice')}
                            {...UNIT_PRICE_INPUT_PROPS}
                            value={item.unitPrice}
                            onChange={(value) => onItemChange(itemKey, 'unitPrice', value)}
                            // Price follows the persisted purchase pair, not the
                            // stock-movement unit. The suffix makes the supplier
                            // invoice basis explicit.
                            addonAfter={`/ ${formatUnit(pricingUnit, i18n.language)}`}
                            style={{ width: '100%' }}
                            {...POLISH_DECIMAL_INPUT_NUMBER_PROPS}
                          />
                        </Tooltip>
                      ) : (
                        <Typography.Text>
                          {item.unitPrice === undefined
                            ? '-'
                            : `${formatCurrencyUnitPrice(item.unitPrice, item.currency || 'PLN')} / ${formatUnit(pricingUnit, i18n.language)}`}
                        </Typography.Text>
                      )}
                    </div>

                    <div className="pz-item-field pz-item-field--total-price">
                      <Typography.Text className="pz-item-field__label">
                        {t(item.purchaseInvoiceItemId ? 'pz:fields.invoiceNetValue' : 'pz:itemFields.totalPrice')}
                      </Typography.Text>
                      <Typography.Text strong>
                        {formatCurrency(lineTotal, item.currency || 'PLN')}
                      </Typography.Text>
                    </div>
                    {item.acceptedReceiptCostPLN != null && (
                      <div className="pz-item-field">
                        <Typography.Text className="pz-item-field__label">{t('pz:fields.acceptedReceiptCost')}</Typography.Text>
                        <Typography.Text strong>{formatCurrency(item.acceptedReceiptCostPLN, 'PLN')}</Typography.Text>
                      </div>
                    )}
                  </>
                )}

                <div className="pz-item-field pz-item-field--notes">
                  <Typography.Text className="pz-item-field__label">
                    {t('pz:itemFields.notes')}
                  </Typography.Text>
                  {isEditMode ? (
                    <Input
                      aria-label={t('pz:itemFields.notes')}
                      value={item.notes}
                      onChange={(event) => onItemChange(itemKey, 'notes', event.target.value)}
                      placeholder={t('pz:itemFields.notes')}
                    />
                  ) : (
                    <Typography.Text>{item.notes}</Typography.Text>
                  )}
                </div>
              </div>
            </article>
          );
        })}
      </div>

      <div className="pz-items-summary" data-testid="pz-items-summary">
        <Typography.Text strong>{t('common:total')}</Typography.Text>
        <div className="pz-items-summary__values">
          <Typography.Text>
            {t('pz:fields.totalQuantity')}: <strong>{quantityTotalsLabel || '-'}</strong>
          </Typography.Text>
          {canViewPurchasePrices && (
            <>
              <Typography.Text>
                {t(isInvoiceReceipt ? 'pz:fields.invoiceNetValue' : 'pz:fields.totalValue')}: <strong>{formatCurrency(totalValue.amount, totalValue.currency)}</strong>
              </Typography.Text>
              {isInvoiceReceipt && acceptedReceiptCostPLN != null && (
                <Typography.Text>
                  {t('pz:fields.acceptedReceiptCost')}: <strong>{formatCurrency(acceptedReceiptCostPLN, 'PLN')}</strong>
                </Typography.Text>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PZItemsSection;
